import { TrendingUp, Star } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { useParentChildren } from '@/hooks/use-parent-data';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

export default function ChildrenListPage() {
  const { data: children, isLoading } = useParentChildren();
  const navigate = useNavigate();

  // If all linked profiles are self, this is an adult student — adapt the heading
  const isAdultStudent =
    !isLoading && children != null && children.length > 0 &&
    children.every(c => c.relationshipType === 'self');

  return (
    <div className="space-y-6">
      <PageHeader
        title={isAdultStudent ? 'My Profile' : 'My Players'}
        subtitle={isAdultStudent ? 'View your training progress and development' : "View your players' progress and development"}
      />

      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : children && children.length > 0 ? (
        <div className="grid gap-4">
          {children.map(child => (
            <Card
              key={child.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/parent/children/${child.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-xl">
                      {child.firstName[0]}{child.lastName[0]}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">
                        {child.firstName} {child.lastName}
                      </h2>
                      <div className="flex items-center gap-2 mt-1">
                        {child.level && (
                          <Badge variant="outline" className="capitalize">
                            {child.level}
                          </Badge>
                        )}
                        {child.utr && (
                          <Badge variant="secondary" className="text-primary bg-primary/10">
                            UTR: {child.utr}
                          </Badge>
                        )}
                        {child.relationshipType && child.relationshipType !== 'self' && (
                          <span className="text-sm text-muted-foreground capitalize">
                            ({child.relationshipType})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-6 text-center">
                    <div>
                      <div className="flex items-center gap-1 text-muted-foreground mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs">Attendance</span>
                      </div>
                      <p className={`text-2xl font-bold ${
                        child.attendancePercent >= 80 ? 'text-green-600' :
                        child.attendancePercent >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {child.attendancePercent}%
                      </p>
                    </div>
                    {child.lastEvalAvg !== null && (
                      <div>
                        <div className="flex items-center gap-1 text-muted-foreground mb-1">
                          <Star className="w-4 h-4" />
                          <span className="text-xs">Last Eval</span>
                        </div>
                        <p className="text-2xl font-bold text-primary">
                          {child.lastEvalAvg}/4
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>No Players Linked</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              No players are currently linked to your account. Please contact your club
              to get your players registered and linked to your parent/guardian account.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
